'use client';

import type { ReactNode } from 'react';
import Image from '@/components/ui/Image';
import {
  BankIcon,
  CheckIcon,
  CircleNotchIcon,
  WarningIcon,
  XIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getBankBrand } from '@/lib/bank-brand';
import type { ImportAlert, ImportStep, StepState } from './ImportProgress.helpers';

export type { ImportAlert, ImportStep, StepState } from './ImportProgress.helpers';

export interface ImportProgressProps {
  variant: 'vertical' | 'horizontal';
  steps: ImportStep[];
  alert?: ImportAlert;
  connectorName?: string | null;
  connectorImage?: string | null;
  onClose?: () => void;
  hideClose?: boolean;
}

const STEP_DOT: Record<StepState, string> = {
  pending: 'bg-surface-sunken text-text-tertiary border border-border-strong',
  active: 'bg-warning-surface text-warning-text border-2 border-warning-border',
  done: 'bg-success text-success-foreground',
  error: 'bg-error text-destructive-foreground',
  warning: 'bg-warning text-warning-foreground',
};

const STEP_LABEL_COLOR: Record<StepState, string> = {
  pending: 'text-text-tertiary',
  active: 'text-text-primary',
  done: 'text-text-primary',
  error: 'text-error-text',
  warning: 'text-warning-text',
};

const STEP_CONNECTOR: Record<StepState, string> = {
  pending: 'bg-border-strong',
  active: 'bg-border-strong',
  done: 'bg-success-border',
  error: 'bg-error-border',
  warning: 'bg-warning-border',
};

function StepIcon({ state, index }: { state: StepState; index: number }) {
  switch (state) {
    case 'done':
      return <CheckIcon size={14} weight="bold" />;
    case 'active':
      return <CircleNotchIcon size={14} weight="bold" className="animate-spin" />;
    case 'error':
      return <XIcon size={14} weight="bold" />;
    case 'warning':
      return <WarningIcon size={14} weight="fill" />;
    case 'pending':
    default:
      return <span className="text-2xs font-bold">{index + 1}</span>;
  }
}

function AlertBanner({ alert }: { alert: ImportAlert }) {
  const cfg =
    alert.tone === 'amber'
      ? {
          bg: 'bg-warning-surface border-warning-border',
          text: 'text-warning-text',
          icon: <WarningIcon size={16} weight="fill" className="text-warning" />,
          btn: 'border-warning-border text-warning-text hover:bg-warning-surface',
        }
      : {
          bg: 'bg-error-surface border-error-border',
          text: 'text-error-text',
          icon: <WarningIcon size={16} weight="fill" className="text-error" />,
          btn: 'border-error-border text-error-text hover:bg-error-surface',
        };

  return (
    <div className={cn('flex flex-wrap items-start gap-3 rounded-xl border p-3', cfg.bg)}>
      <div className="mt-0.5 shrink-0">{cfg.icon}</div>
      <div className={cn('min-w-0 flex-1', cfg.text)}>
        <p className="text-sm font-bold">{alert.title}</p>
        <p className="mt-0.5 text-xs">{alert.message}</p>
      </div>
      {alert.cta && (
        <Button variant="outline" size="sm" onClick={alert.cta.onClick} className={cn('bg-surface text-xs', cfg.btn)}>
          {alert.cta.label}
        </Button>
      )}
    </div>
  );
}

function VerticalStepper({ steps, connectorName, connectorImage, alert, onClose, hideClose }: ImportProgressProps) {
  const brand = getBankBrand(connectorName ?? undefined);
  const textOnBrand = brand.textOnBrand === 'light' ? 'text-white' : 'text-text-primary';
  const subOnBrand = brand.textOnBrand === 'light' ? 'text-white/85' : 'text-text-primary/70';
  const allDone = steps.every((s) => s.state === 'done');

  return (
    <div data-tokens="v2" className="flex flex-col">
      <div
        className="relative px-6 pt-7 pb-6"
        style={{ backgroundImage: `linear-gradient(135deg, ${brand.from}, ${brand.to})` }}
      >
        <div className="flex items-start gap-3">
          {connectorImage ? (
            <div className="flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-surface p-1.5 shadow-md">
              <Image
                src={connectorImage}
                alt={connectorName ?? ''}
                width={36}
                height={36}
                className="object-contain"
                unoptimized
              />
            </div>
          ) : (
            <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/20 text-white">
              <BankIcon size={22} weight="bold" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className={cn('text-lg font-semibold', textOnBrand)}>
              {allDone ? 'Tudo pronto!' : connectorName ? `Conectando ${connectorName}` : 'Conectando sua conta'}
            </h3>
            <p className={cn('mt-1 text-xs', subOnBrand)}>
              {allDone
                ? 'Seus dados já estão disponíveis no Vertice.'
                : 'A importação geralmente leva de 10 a 60 segundos.'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {alert && <AlertBanner alert={alert} />}

        <ol className="space-y-0">
          {steps.map((step, idx) => {
            const isLast = idx === steps.length - 1;
            return (
              <li key={step.id} className="relative flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'z-10 flex size-6 shrink-0 items-center justify-center rounded-full',
                      STEP_DOT[step.state],
                    )}
                  >
                    <StepIcon state={step.state} index={idx} />
                  </div>
                  {!isLast && (
                    <div className={cn('mt-0.5 h-8 w-px', STEP_CONNECTOR[step.state])} />
                  )}
                </div>
                <div className={cn('min-w-0 flex-1', isLast ? 'pb-0' : 'pb-4')}>
                  <p className={cn('text-sm/5 font-bold', STEP_LABEL_COLOR[step.state])}>{step.label}</p>
                  <p className="mt-0.5 text-xs/5 text-text-secondary">{step.description}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {!allDone && (
          <div className="rounded-[var(--radius-md)] bg-surface-sunken px-3 py-2.5">
            <div className="flex items-start gap-2">
              <ShieldCheckIcon size={14} weight="fill" className="mt-0.5 shrink-0 text-success" />
              <p className="text-2xs leading-relaxed text-text-secondary">
                Você pode fechar esta janela — a importação continua em segundo plano e o card é atualizado sozinho.
              </p>
            </div>
          </div>
        )}

        {!hideClose && onClose && (
          <Button variant="secondary" onClick={onClose} className="w-full" size="sm">
            {allDone ? 'Fechar' : 'Fechar e acompanhar pela página'}
          </Button>
        )}
      </div>
    </div>
  );
}

function HorizontalStepper({ steps, connectorName, connectorImage, alert }: ImportProgressProps) {
  const brand = getBankBrand(connectorName ?? undefined);
  const allDone = steps.every((s) => s.state === 'done');

  return (
    <Card data-tokens="v2" variant="flat" className="overflow-hidden rounded-[var(--radius-3xl)] border-border bg-surface p-0 shadow-[var(--shadow-1)]">
      <div
        className="flex flex-wrap items-center gap-4 px-4 py-3 sm:px-5"
        style={{ backgroundImage: `linear-gradient(135deg, ${brand.from}10, ${brand.to}18)` }}
      >
        <div className="flex min-w-0 items-center gap-3">
          {connectorImage ? (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-surface p-1 shadow-sm">
              <Image
                src={connectorImage}
                alt={connectorName ?? ''}
                width={30}
                height={30}
                className="object-contain"
                unoptimized
              />
            </div>
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-surface-sunken text-brand-primary">
              <BankIcon size={20} weight="bold" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary">
              {allDone ? 'Importação concluída' : connectorName ? `Importando ${connectorName}` : 'Importando dados'}
            </p>
            <p className="text-2xs text-text-secondary">Acompanhe o andamento ao lado.</p>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-1 sm:gap-2">
          {steps.map((step, idx) => {
            const isLast = idx === steps.length - 1;
            return (
              <div key={step.id} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
                <div className="flex min-w-0 flex-col items-center gap-1">
                  <div
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full',
                      STEP_DOT[step.state],
                    )}
                  >
                    <StepIcon state={step.state} index={idx} />
                  </div>
                  <span
                    className={cn(
                      'hidden truncate text-center text-2xs font-bold tracking-wide uppercase sm:block',
                      STEP_LABEL_COLOR[step.state],
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {!isLast && <div className={cn('h-px flex-1', STEP_CONNECTOR[step.state])} />}
              </div>
            );
          })}
        </div>

        {(() => {
          const current =
            steps.find((s) => s.state === 'active') ??
            [...steps].reverse().find((s) => s.state === 'done');
          if (!current) return null;
          return (
            <p className="basis-full text-2xs font-semibold text-text-primary sm:hidden">
              {current.label}
              {current.state === 'active' ? '…' : ''}
            </p>
          );
        })()}
      </div>

      {alert && (
        <div className="border-t border-divider p-3">
          <AlertBanner alert={alert} />
        </div>
      )}
    </Card>
  );
}

export function ImportProgress(props: ImportProgressProps): ReactNode {
  if (props.variant === 'horizontal') return <HorizontalStepper {...props} />;
  return <VerticalStepper {...props} />;
}
